import { useState, useRef, useEffect } from 'react';
import { Gear, CyclicAction, BinaryAction } from '@components/operatorLobby/ControlStream';

const RightButtonMapping = {
    FlipTriggerSwitch : 0,
    FlipTriggerDown : 1,
    TriggerHalf : 2,
    TriggerFull : 3,
    ThumbPress : 4,
    BlackRoundButton : 5,
    RampPress : 6,
    RampUp : 7,
    RampRight : 8,
    RampDown : 9,
    RampLeft : 10,
    RedRoundButton : 11,
    PyramidPress : 12,
    PyramidUp : 13,
    PyramidRight : 14,
    PyramidDown : 15,
    PyramidLeft : 16,
    RightSideRampPress : 17,
    RightSideRampUp : 18,
    RightSideRampDown : 19,
    WheelPressFull : 20,
    WheelPressHalf : 21,
    WheelDown: 22,
    WheelUp : 23,
    LeftSideRampPress : 24,
    LeftSideRampUp : 25,
    LeftSideRampRight : 26,
    LeftSideRampDown : 27,
    LeftSideRampLeft : 28,
    BackButton : 29,
    BrakeLever : 30,
}

// Up/Down is y
// Left/Right is x
const AxesMapping = {
    xAxis : 0, // X
    yAxis : 1, // Y 
    zAxis : 2, // Z
    thumbXAxis : 3, // rX
    thumbYAxis : 4, // rY
    // : 5, // ??
    brakeAxis : 6  // Slider
};

export default function useJoystickRight() {

    const gamepadIndexRef = useRef(null);
    const [isConnected, setConnected] = useState(false);
    const [enable, setEnable] = useState(true);
    const [controlUpdate, setControlUpdate] = useState(null);
    const [controlSend, setControlSend] = useState(null);
    const [vehicleStateRequest, setVehicleStateRequest] = useState(null);
    const lastAxisUpdateTime = useRef(Date.now());
    const [goalPosition, setGoalPosition] = useState({enabled: false, position: 0});

    let engineRPMIncrement = 10;

    useEffect(() => {
        function onGamepadConnected(event) {
            const gamepad = event.gamepad;
            if (/R-VPC Stick MT-50CM2/i.test(gamepad.id)) {
                gamepadIndexRef.current = event.gamepad.index;
                setConnected(true);
                console.log(
                    'Gamepad connected at index %d: %s. %d buttons, %d axes.',
                    event.gamepad.index,
                    event.gamepad.id,
                    event.gamepad.buttons.length,
                    event.gamepad.axes.length
                )
            } 
        }

        function onGamepadDisconnected(event) {
            const gamepad = event.gamepad;
            if (/R-VPC Stick MT-50CM2/i.test(gamepad.id)) {
                gamepadIndexRef.current = null;
                setConnected(false);
                console.warn('%s disconnected.', event.gamepad.id);
            }
        }

        window.addEventListener('gamepaddisconnected', onGamepadDisconnected);
        window.addEventListener('gamepadconnected', onGamepadConnected);

        return () => {
            window.removeEventListener('gamepadconnected', onGamepadConnected);
            window.removeEventListener('gamepaddisconnected', onGamepadConnected);
        };
    }, []);

    useEffect(() => {
        if(enable && isConnected && controlUpdate && controlSend && vehicleStateRequest) {
            const gamepad = navigator.getGamepads()[gamepadIndexRef.current];

            let lastGamepadState = {
                buttons: gamepad.buttons.map((button) => button.pressed),
                axes: gamepad.axes.map((value) => value), // Necessary to avoid sharing array
            }

            function onButtonChanged(index, pressed) {
                if(index === RightButtonMapping.Horn) {
                    //controlUpdate(control => ({ ...control, horn: pressed ? BinaryAction.Set : BinaryAction.Clear }));
                }
            }
        
            function onButtonDown(index) {
                switch(index) {
                    case RightButtonMapping.RedRoundButton:
                        vehicleStateRequest('emergency');  
                        break;
                    case RightButtonMapping.PyramidUp:
                        break;
                    case RightButtonMapping.PyramidLeft:
                        controlUpdate(control => ({ ...control, enlargementSpeed: -Math.abs(1.0) }));
                        break;
                    case RightButtonMapping.PyramidRight:
                        controlUpdate(control => ({ ...control, enlargementSpeed: Math.abs(1.0) }));
                        break;
                    case RightButtonMapping.PyramidDown:
                        break;
                    case RightButtonMapping.WheelUp:
                        controlUpdate(control => ({ ...control, increaseRPM: BinaryAction.Flip }));
                        break;
                    case RightButtonMapping.WheelDown:
                        controlUpdate(control => ({ ...control, decreaseRPM: BinaryAction.Flip }));
                        break;
                    case RightButtonMapping.BlackRoundButton:
                        controlUpdate(control => ({ ...control, neutralFlotting: true }));
                        break;
                    case RightButtonMapping.RampRight:
                        break;
                    case RightButtonMapping.RampLeft:
                        break;
                    case RightButtonMapping.BrakeLever:
                        
                        break; 
                    case RightButtonMapping.TriggerHalf:
                        controlUpdate(control => ({ ...control, engine: BinaryAction.Flip }));
                        break;
                    case RightButtonMapping.FlipTriggerDown:
                        controlUpdate(control => ({ ...control, ignition: BinaryAction.Set }));
                        break;
                    default:
                        break;
                }
            }
        
            function onButtonUp(index) {
                switch(index) {
                    case RightButtonMapping.PyramidRight:
                        controlUpdate(control => ({ ...control, enlargementSpeed: 0.0 }));
                        break;
                    case RightButtonMapping.PyramidLeft:
                        controlUpdate(control => ({ ...control, enlargementSpeed: 0.0 }));
                        break;
                    case RightButtonMapping.BlackRoundButton:
                        controlUpdate(control => ({ ...control, neutralFlotting: false }));
                        break;
                    default:
                        break;
                }
            }

            function onAxisChanged(index, _oldValue, value) {
                switch(index) {
                    case AxesMapping.xAxis:                    
                        // Left is negative
                        controlUpdate(control => ({ ...control, translationLeftRightSpeed: value }));
                        break;
                    case AxesMapping.yAxis:
                        // Joystick up is negative value
                        controlUpdate(control => ({ ...control, armUpDownSpeed: value }));
                        break;
                    case AxesMapping.zAxis:
                        // Left is negative
                        break;
                    case AxesMapping.thumbXAxis:
                        // Left is negative
                        controlUpdate(control => ({ ...control, coverFlailUpDownSpeed: -value })); // Open / Close bucket
                        break;
                    case AxesMapping.thumbYAxis:
                        // Up is negative
                        controlUpdate(control => ({ ...control, hydraulicAuxSpeed: value }));
                        break;
                    case AxesMapping.brakeAxis:
                        // Full press is -1, not pressed is 1
                        break;
                    default:
                        break;
                }
            }

            let animationRequestId = window.requestAnimationFrame(poll);

            function poll() {
                const gamepad = navigator.getGamepads()[gamepadIndexRef.current];
                if(gamepad) {
                    let hasControlBeenUpdated = false;

                    for(let i = 0; i < gamepad.buttons.length; i++) {
                        const oldValue = lastGamepadState.buttons[i];
                        const newValue = gamepad.buttons[i].pressed
                        lastGamepadState.buttons[i] = newValue;
                        if(oldValue !== newValue) {
                            if(newValue) {
                                onButtonDown(i);
                            } else {
                                onButtonUp(i);
                            }
                            onButtonChanged(i, newValue);
                            console.log(`Button ${i}`);  
                            hasControlBeenUpdated = true;
                        }
                    }

                    const now = Date.now(); // Downsample the rate
                    const deltaTime = now - lastAxisUpdateTime.current;
                    const thresholdTime = 100; // 10Hz
                    
                    for(let i = 0; i < gamepad.axes.length; i++) {
                        const oldValue = lastGamepadState.axes[i];
                        const newValue = gamepad.axes[i];
                        if(oldValue !== newValue) {
                            if(deltaTime > thresholdTime || oldValue === 0.0 || newValue === 0.0 || Math.abs(oldValue - newValue) > 0.1) { // Ignore timer when there is a change from or to zero or if change is more than 10%
                                lastGamepadState.axes[i] = newValue;
                                onAxisChanged(i, oldValue, newValue);
                                // console.log(`Axis ${i}`);  
                                hasControlBeenUpdated = true;
                            }
                        }
                    }

                    if(deltaTime > thresholdTime) {
                        lastAxisUpdateTime.current = now;
                    }

                    if(hasControlBeenUpdated) {
                        controlSend();
                    }

                    animationRequestId = window.requestAnimationFrame(poll);
                }
            }
            
            return () => {
                window.cancelAnimationFrame(animationRequestId);
            };
        }
    }, [isConnected, controlUpdate, controlSend, vehicleStateRequest, enable]);

    useEffect(() => {
        if (window.electron) {
            window.electron.steeringWheelSetGoalPosition(goalPosition)
        }
    }, [goalPosition])

    return {
        isConnected,
        enable,
        setEnable,
        setControlUpdate,
        setControlSend,
        setVehicleStateRequest,
        setGoalPosition,
    };
}
